import type { DashboardWatchlistEntry } from "../../../hooks/useDashboardWatchlist";
import { getCurrencyFromExchange, isPenceCurrency } from "../../../utils/stockPriceFormatter";

// The dashboard watchlist's sort rules as a pure function, so the ordering
// every card position hangs off is unit-testable in isolation. Sorting is
// CLIENT-SIDE by design: /dashboard-watchlist already returns every field
// (Ben, 2026-09-01 — no sort param goes up to the API).

export type WatchlistSortKey =
  | "manual"
  | "change"
  | "changePct"
  | "marketCap"
  | "symbol"
  | "name";
export type WatchlistSortDir = "asc" | "desc";

// Selecting a fresh sort key starts from the direction people mean by it:
// financial values biggest-first (the shared useSorting convention), the
// alphabetical keys A-first. Manual desc = newest-added first, which is the
// server's natural order — the app's behaviour before this feature existed.
export const DEFAULT_SORT_DIR: Record<WatchlistSortKey, WatchlistSortDir> = {
  manual: "desc",
  change: "desc",
  changePct: "desc",
  marketCap: "desc",
  symbol: "asc",
  name: "asc",
};

const textCompare = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

// Numeric sort values; null = "no data", which always sorts LAST regardless
// of direction (the useSorting nulls-last convention). marketCap uses the
// API's own missing-data sentinel: it falls back to 0 when both profile and
// quote lack a cap, and a $0 company is not a real value to rank.
const numericValue = (
  entry: DashboardWatchlistEntry,
  sort: "change" | "changePct" | "marketCap"
): number | null => {
  let raw =
    sort === "change" ? entry.change : sort === "changePct" ? entry.changePercentage : entry.marketCap;
  // LSE quotes arrive in PENCE ("GBX"), so their $-change deltas are 100×
  // the comparable pound value — normalise before comparing, or a 40p move
  // outranks a $35 one. (Full cross-currency FX normalisation is out of
  // scope; the unit error was the real distortion, ~1× FX drift is not.)
  if (!Number.isFinite(raw)) return null;
  if (sort === "change" && isPenceCurrency(getCurrencyFromExchange(entry.exchange))) {
    raw = raw / 100;
  }
  if (sort === "marketCap" && raw <= 0) return null;
  return raw;
};

export const sortWatchlistEntries = (
  entries: DashboardWatchlistEntry[],
  sort: WatchlistSortKey,
  dir: WatchlistSortDir
): DashboardWatchlistEntry[] => {
  const flip = dir === "asc" ? -1 : 1;
  const sorted = [...entries];

  if (sort === "manual") {
    // "As added". ISO timestamps compare lexically; the ticker tiebreak
    // matters because seeded lists share one bulkCreate timestamp, and
    // bare createdAt would shuffle between requests.
    sorted.sort((a, b) => {
      const byCreated = String(b.createdAt).localeCompare(String(a.createdAt)) * flip;
      if (byCreated !== 0) return byCreated;
      return textCompare(a.ticker, b.ticker);
    });
    return sorted;
  }

  if (sort === "symbol" || sort === "name") {
    sorted.sort(
      (a, b) =>
        (sort === "symbol"
          ? textCompare(b.ticker, a.ticker)
          : textCompare(b.companyName, a.companyName)) * flip
    );
    return sorted;
  }

  sorted.sort((a, b) => {
    const aValue = numericValue(a, sort);
    const bValue = numericValue(b, sort);
    if (aValue == null && bValue == null) return textCompare(a.ticker, b.ticker);
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    return (bValue - aValue) * flip;
  });
  return sorted;
};
